/**
 * Client Portal Page
 * Private area where clients can access their documents, service history, and request new services
 */

import { useState } from "react";
import styles from "./ClientPortalPage.module.css";
import { Icon } from "@/components/ui/Icon";
import { AlertModal } from "@/components/ui/AlertModal";
import { useAuth } from "@/contexts/AuthContext";

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  nif: string;
}

interface Document {
  id: string;
  type: "certificate" | "workReport" | "invoice";
  title: string;
  date: string;
  serviceId?: string;
  size: string;
  status: "available" | "processing";
}

interface Service {
  id: string;
  date: string;
  type: string;
  technician: string;
  status: "completed" | "scheduled" | "cancelled";
  address: string;
  description: string;
  hasCertificate: boolean;
  hasWorkReport: boolean;
  hasInvoice: boolean;
}

interface NewServiceRequest {
  type: string;
  urgency: "low" | "medium" | "high";
  address: string;
  description: string;
  preferredDate: string;
  preferredTime: string;
}

// Mock authenticated client
const mockClient: Client = {
  id: "CLI-001",
  name: "Hotel Meridian",
  email: "info@hotelmeridian.com",
  phone: "+34 912 345 678",
  address: "Calle Mayor 123, 28013 Madrid",
  nif: "B12345678",
};

// Mock documents
const mockDocuments: Document[] = [
  {
    id: "DOC-001",
    type: "certificate",
    title: "Certificado DDD - Enero 2026",
    date: "2026-01-15",
    serviceId: "SRV-124",
    size: "245 KB",
    status: "available",
  },
  {
    id: "DOC-002",
    type: "workReport",
    title: "Parte de Trabajo - Control de Roedores",
    date: "2026-01-15",
    serviceId: "SRV-124",
    size: "189 KB",
    status: "available",
  },
  {
    id: "DOC-003",
    type: "invoice",
    title: "Factura 2026-001",
    date: "2026-01-16",
    serviceId: "SRV-124",
    size: "156 KB",
    status: "available",
  },
  {
    id: "DOC-004",
    type: "certificate",
    title: "Certificado DDD - Diciembre 2025",
    date: "2025-12-20",
    serviceId: "SRV-118",
    size: "238 KB",
    status: "available",
  },
  {
    id: "DOC-005",
    type: "invoice",
    title: "Factura 2025-142",
    date: "2025-12-21",
    serviceId: "SRV-118",
    size: "152 KB",
    status: "available",
  },
];

// Mock services
const mockServices: Service[] = [
  {
    id: "SRV-124",
    date: "2026-01-15",
    type: "Control de Roedores",
    technician: "Juan Pérez",
    status: "completed",
    address: "Calle Mayor 123, 28013 Madrid",
    description: "Inspección trimestral y renovación de cebos",
    hasCertificate: true,
    hasWorkReport: true,
    hasInvoice: true,
  },
  {
    id: "SRV-123",
    date: "2026-01-20",
    type: "Desinsectación",
    technician: "María García",
    status: "scheduled",
    address: "Calle Mayor 123, 28013 Madrid",
    description: "Tratamiento preventivo de insectos rastreros",
    hasCertificate: false,
    hasWorkReport: false,
    hasInvoice: false,
  },
  {
    id: "SRV-118",
    date: "2025-12-20",
    type: "Control de Roedores",
    technician: "Juan Pérez",
    status: "completed",
    address: "Calle Mayor 123, 28013 Madrid",
    description: "Inspección trimestral y mantenimiento",
    hasCertificate: true,
    hasWorkReport: true,
    hasInvoice: true,
  },
  {
    id: "SRV-115",
    date: "2025-11-18",
    type: "Desinfección",
    technician: "Carlos Rodríguez",
    status: "completed",
    address: "Calle Mayor 123, 28013 Madrid",
    description: "Desinfección de áreas comunes",
    hasCertificate: true,
    hasWorkReport: true,
    hasInvoice: true,
  },
];

/**
 * Client Portal Page Component
 */
export function ClientPortalPage(): React.ReactElement {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "documents" | "services" | "request"
  >("documents");
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });

  const [newRequest, setNewRequest] = useState<NewServiceRequest>({
    type: "",
    urgency: "medium",
    address: mockClient.address,
    description: "",
    preferredDate: "",
    preferredTime: "",
  });

  // Download document handler
  const handleDownloadDocument = (doc: Document) => {
    setAlertModal({
      isOpen: true,
      message: `Descargando ${doc.title}...`,
      type: "success",
    });
    // In production: trigger actual download
  };

  // Submit new service request
  const handleSubmitRequest = () => {
    if (!newRequest.type || !newRequest.description) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, completa el tipo de servicio y la descripción",
        type: "warning",
      });
      return;
    }

    if (!newRequest.preferredDate) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, indica una fecha preferida",
        type: "warning",
      });
      return;
    }

    setAlertModal({
      isOpen: true,
      message:
        "Solicitud enviada correctamente. Nos pondremos en contacto contigo en breve.",
      type: "success",
    });

    // Reset form
    setNewRequest({
      type: "",
      urgency: "medium",
      address: mockClient.address,
      description: "",
      preferredDate: "",
      preferredTime: "",
    });
  };

  const getDocumentIcon = (type: Document["type"]) => {
    switch (type) {
      case "certificate":
        return "shield";
      case "workReport":
        return "clipboard";
      case "invoice":
        return "fileText";
      default:
        return "document";
    }
  };

  const getDocumentTypeLabel = (type: Document["type"]) => {
    switch (type) {
      case "certificate":
        return "Certificado";
      case "workReport":
        return "Parte de Trabajo";
      case "invoice":
        return "Factura";
      default:
        return "Documento";
    }
  };

  const getServiceStatusLabel = (status: Service["status"]) => {
    switch (status) {
      case "completed":
        return "Completado";
      case "scheduled":
        return "Programado";
      case "cancelled":
        return "Cancelado";
      default:
        return status;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.welcomeSection}>
          <h1>Área Privada de Cliente</h1>
          <p className={styles.subtitle}>Bienvenido, {mockClient.name}</p>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.clientInfo}>
            <div className={styles.infoItem}>
              <Icon name="mail" size={16} />
              <span>{mockClient.email}</span>
            </div>
            <div className={styles.infoItem}>
              <Icon name="phone" size={16} />
              <span>{mockClient.phone}</span>
            </div>
          </div>
          <button className={styles.logoutButton} onClick={() => logout()}>
            <Icon name="power" size={18} />
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "documents" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("documents")}
        >
          <Icon name="download" size={20} />
          Mis Documentos
        </button>
        <button
          className={`${styles.tab} ${activeTab === "services" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("services")}
        >
          <Icon name="clipboard" size={20} />
          Histórico de Servicios
        </button>
        <button
          className={`${styles.tab} ${activeTab === "request" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("request")}
        >
          <Icon name="plus" size={20} />
          Solicitar Servicio
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Documentos Disponibles</h2>
              <p className={styles.sectionSubtitle}>
                Descarga certificados, partes de trabajo y facturas
              </p>
            </div>

            {/* Certificates Section */}
            <div className={styles.documentSection}>
              <h3 className={styles.documentSectionTitle}>
                <Icon name="shield" size={20} />
                Certificados
              </h3>
              <div className={styles.documentGrid}>
                {mockDocuments
                  .filter((doc) => doc.type === "certificate")
                  .map((doc) => (
                    <div key={doc.id} className={styles.documentCard}>
                      <div className={styles.documentIcon}>
                        <Icon name={getDocumentIcon(doc.type)} size={32} />
                      </div>
                      <div className={styles.documentInfo}>
                        <span className={styles.documentType}>
                          {getDocumentTypeLabel(doc.type)}
                        </span>
                        <h3 className={styles.documentTitle}>{doc.title}</h3>
                        <div className={styles.documentMeta}>
                          <span>
                            <Icon name="calendar" size={14} />
                            {new Date(doc.date).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <span>
                            <Icon name="document" size={14} />
                            {doc.size}
                          </span>
                        </div>
                      </div>
                      <button
                        className={styles.downloadButton}
                        onClick={() => handleDownloadDocument(doc)}
                        disabled={doc.status === "processing"}
                      >
                        <Icon name="download" size={20} />
                        {doc.status === "available"
                          ? "Descargar"
                          : "Procesando..."}
                      </button>
                    </div>
                  ))}
              </div>
            </div>

            {/* Work Reports Section */}
            <div className={styles.documentSection}>
              <h3 className={styles.documentSectionTitle}>
                <Icon name="clipboard" size={20} />
                Partes de Trabajo
              </h3>
              <div className={styles.documentGrid}>
                {mockDocuments
                  .filter((doc) => doc.type === "workReport")
                  .map((doc) => (
                    <div key={doc.id} className={styles.documentCard}>
                      <div className={styles.documentIcon}>
                        <Icon name={getDocumentIcon(doc.type)} size={32} />
                      </div>
                      <div className={styles.documentInfo}>
                        <span className={styles.documentType}>
                          {getDocumentTypeLabel(doc.type)}
                        </span>
                        <h3 className={styles.documentTitle}>{doc.title}</h3>
                        <div className={styles.documentMeta}>
                          <span>
                            <Icon name="calendar" size={14} />
                            {new Date(doc.date).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <span>
                            <Icon name="document" size={14} />
                            {doc.size}
                          </span>
                        </div>
                      </div>
                      <button
                        className={styles.downloadButton}
                        onClick={() => handleDownloadDocument(doc)}
                        disabled={doc.status === "processing"}
                      >
                        <Icon name="download" size={20} />
                        {doc.status === "available"
                          ? "Descargar"
                          : "Procesando..."}
                      </button>
                    </div>
                  ))}
              </div>
            </div>

            {/* Invoices Section */}
            <div className={styles.documentSection}>
              <h3 className={styles.documentSectionTitle}>
                <Icon name="fileText" size={20} />
                Facturas
              </h3>
              <div className={styles.documentGrid}>
                {mockDocuments
                  .filter((doc) => doc.type === "invoice")
                  .map((doc) => (
                    <div key={doc.id} className={styles.documentCard}>
                      <div className={styles.documentIcon}>
                        <Icon name={getDocumentIcon(doc.type)} size={32} />
                      </div>
                      <div className={styles.documentInfo}>
                        <span className={styles.documentType}>
                          {getDocumentTypeLabel(doc.type)}
                        </span>
                        <h3 className={styles.documentTitle}>{doc.title}</h3>
                        <div className={styles.documentMeta}>
                          <span>
                            <Icon name="calendar" size={14} />
                            {new Date(doc.date).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <span>
                            <Icon name="document" size={14} />
                            {doc.size}
                          </span>
                        </div>
                      </div>
                      <button
                        className={styles.downloadButton}
                        onClick={() => handleDownloadDocument(doc)}
                        disabled={doc.status === "processing"}
                      >
                        <Icon name="download" size={20} />
                        {doc.status === "available"
                          ? "Descargar"
                          : "Procesando..."}
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Services History Tab */}
        {activeTab === "services" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Histórico de Servicios</h2>
              <p className={styles.sectionSubtitle}>
                Consulta todos los servicios realizados
              </p>
            </div>

            <div className={styles.servicesTimeline}>
              {mockServices.map((service) => (
                <div key={service.id} className={styles.serviceCard}>
                  <div className={styles.serviceHeader}>
                    <div className={styles.serviceMain}>
                      <span
                        className={`${styles.serviceStatus} ${styles[service.status]}`}
                      >
                        {getServiceStatusLabel(service.status)}
                      </span>
                      <h3 className={styles.serviceType}>{service.type}</h3>
                      <p className={styles.serviceId}>ID: {service.id}</p>
                    </div>
                    <div className={styles.serviceDate}>
                      <Icon name="calendar" size={20} />
                      <span>
                        {new Date(service.date).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  </div>

                  <div className={styles.serviceDetails}>
                    <div className={styles.serviceDetailItem}>
                      <Icon name="users" size={16} />
                      <span>
                        <strong>Técnico:</strong> {service.technician}
                      </span>
                    </div>
                    <div className={styles.serviceDetailItem}>
                      <Icon name="mapPin" size={16} />
                      <span>
                        <strong>Dirección:</strong> {service.address}
                      </span>
                    </div>
                    <div className={styles.serviceDetailItem}>
                      <Icon name="document" size={16} />
                      <span>
                        <strong>Descripción:</strong> {service.description}
                      </span>
                    </div>
                  </div>

                  {service.status === "completed" && (
                    <div className={styles.serviceDocuments}>
                      <p className={styles.documentsLabel}>
                        Documentos disponibles:
                      </p>
                      <div className={styles.documentBadges}>
                        {service.hasCertificate && (
                          <span className={styles.documentBadge}>
                            <Icon name="shield" size={14} />
                            Certificado
                          </span>
                        )}
                        {service.hasWorkReport && (
                          <span className={styles.documentBadge}>
                            <Icon name="clipboard" size={14} />
                            Parte de Trabajo
                          </span>
                        )}
                        {service.hasInvoice && (
                          <span className={styles.documentBadge}>
                            <Icon name="fileText" size={14} />
                            Factura
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Request Service Tab */}
        {activeTab === "request" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Solicitar Nuevo Servicio</h2>
              <p className={styles.sectionSubtitle}>
                Envíanos una solicitud y nos pondremos en contacto contigo
              </p>
            </div>

            <div className={styles.requestForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Tipo de Servicio <span className={styles.required}>*</span>
                  </label>
                  <select
                    className={styles.select}
                    value={newRequest.type}
                    onChange={(e) =>
                      setNewRequest({ ...newRequest, type: e.target.value })
                    }
                  >
                    <option value="">Selecciona un tipo</option>
                    <option value="Control de Roedores">
                      Control de Roedores
                    </option>
                    <option value="Desinsectación">Desinsectación</option>
                    <option value="Desinfección">Desinfección</option>
                    <option value="Control de Aves">Control de Aves</option>
                    <option value="Control de Termitas">
                      Control de Termitas
                    </option>
                    <option value="Legionella">Tratamiento Legionella</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Urgencia</label>
                  <select
                    className={styles.select}
                    value={newRequest.urgency}
                    onChange={(e) =>
                      setNewRequest({
                        ...newRequest,
                        urgency: e.target.value as NewServiceRequest["urgency"],
                      })
                    }
                  >
                    <option value="low">Baja - Mantenimiento rutinario</option>
                    <option value="medium">Media - En los próximos días</option>
                    <option value="high">Alta - Urgente</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Dirección del Servicio</label>
                <input
                  type="text"
                  className={styles.input}
                  value={newRequest.address}
                  onChange={(e) =>
                    setNewRequest({ ...newRequest, address: e.target.value })
                  }
                  placeholder="Dirección donde se realizará el servicio"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Fecha Preferida <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    className={styles.input}
                    value={newRequest.preferredDate}
                    onChange={(e) =>
                      setNewRequest({
                        ...newRequest,
                        preferredDate: e.target.value,
                      })
                    }
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Hora Preferida</label>
                  <select
                    className={styles.select}
                    value={newRequest.preferredTime}
                    onChange={(e) =>
                      setNewRequest({
                        ...newRequest,
                        preferredTime: e.target.value,
                      })
                    }
                  >
                    <option value="">Sin preferencia</option>
                    <option value="morning">Mañana (8:00 - 14:00)</option>
                    <option value="afternoon">Tarde (14:00 - 20:00)</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Descripción del Problema{" "}
                  <span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={newRequest.description}
                  onChange={(e) =>
                    setNewRequest({
                      ...newRequest,
                      description: e.target.value,
                    })
                  }
                  placeholder="Describe detalladamente el problema o la incidencia..."
                  rows={5}
                />
              </div>

              <div className={styles.urgencyInfo}>
                <Icon name="alertTriangle" size={20} />
                <div>
                  <strong>Nota sobre urgencias:</strong>
                  <p>
                    Para emergencias fuera de horario, llama al{" "}
                    <a href="tel:+34900123456">900 123 456</a> (24h)
                  </p>
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  className={styles.submitButton}
                  onClick={handleSubmitRequest}
                >
                  <Icon name="check" size={20} />
                  Enviar Solicitud
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* GDPR Notice */}
      <div className={styles.gdprNotice}>
        <Icon name="shield" size={16} />
        <span>
          Tus datos están protegidos según el RGPD. Consulta nuestra{" "}
          <a href="/privacy">Política de Privacidad</a>.
        </span>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() =>
          setAlertModal({ isOpen: false, message: "", type: "info" })
        }
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
}

export default ClientPortalPage;
